import React, { useCallback, useEffect, useState } from "react";

import { toast } from "react-toastify";
import {
	Loader2,
	CreditCard,
	CheckCircle2,
	XCircle,
	AlertTriangle,
	Copy,
} from "lucide-react";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../components/ui/select";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { getBackendUrl } from "../../config";

const GlobalConfig = () => {
	const [config, setConfig] = useState(null);
	const [carregando, setCarregando] = useState(true);
	const [salvando, setSalvando] = useState(false);

	// Segredos ficam em campos à parte: em branco significa "não mexi",
	// e não "apague o que está gravado".
	const [novaChave, setNovaChave] = useState("");
	const [novoToken, setNovoToken] = useState("");
	const [novaChaveStripe, setNovaChaveStripe] = useState("");
	const [novoSegredoStripe, setNovoSegredoStripe] = useState("");
	// Catálogo de países vem do servidor: manter a lista aqui faria a tela
	// oferecer país que o backend não sabe validar.
	const [opcoesFiscais, setOpcoesFiscais] = useState([]);

	const base = String(getBackendUrl() || "").replace(/\/+$/, "");
	const urlWebhook = `${base}/webhooks/asaas`;
	const urlWebhookStripe = `${base}/webhooks/stripe`;

	const carregar = useCallback(async () => {
		setCarregando(true);
		try {
			const { data } = await api.get("/global-config");
			setConfig(data);

			const { data: fiscal } = await api.get("/settings/fiscal");
			setOpcoesFiscais(fiscal?.opcoes || []);
		} catch (err) {
			toastError(err);
		}
		setCarregando(false);
	}, []);

	useEffect(() => {
		carregar();
	}, [carregar]);

	const salvar = async (e) => {
		e.preventDefault();
		setSalvando(true);
		try {
			const { data } = await api.put("/global-config", {
				paymentGateway: config.paymentGateway,
				asaasEnv: config.asaasEnv,
				paymentCurrency: config.paymentCurrency,
				fiscalCountry: config.fiscalCountry || "",
				trialDays: config.trialDays,
				...(novaChave && { asaasApiKey: novaChave }),
				...(novoToken && { asaasWebhookToken: novoToken }),
				...(novaChaveStripe && { stripeSecretKey: novaChaveStripe }),
				stripePublishableKey: config.stripePublishableKey || "",
				...(novoSegredoStripe && {
					stripeWebhookSecret: novoSegredoStripe,
				}),
			});
			setConfig(data);
			setNovaChave("");
			setNovoToken("");
			setNovaChaveStripe("");
			setNovoSegredoStripe("");
			toast.success("Configurações salvas.");
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	if (carregando || !config) {
		return (
			<MainContainer>
				<MainHeader>
					<Title>Configurações globais</Title>
				</MainHeader>
				<div className="flex-1 px-4 pb-4 sm:px-6 sm:pb-6">
					<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
				</div>
			</MainContainer>
		);
	}

	return (
		<MainContainer>
			<MainHeader>
				<Title>Configurações globais</Title>
			</MainHeader>

			<div className="flex-1 overflow-auto px-4 pb-4 sm:px-6 sm:pb-6">
				<p className="mb-4 max-w-3xl text-sm leading-relaxed text-muted-foreground">
					Ajustes da plataforma inteira, não de uma empresa. Quem mexe aqui é
					só quem opera o sistema — o que muda nesta tela vale para todos os
					seus clientes.
				</p>

				<form onSubmit={salvar} className="max-w-2xl space-y-6">
					{/* Gateway */}
					<div className="space-y-4 rounded-xl border bg-card p-5">
						<div className="flex items-center justify-between gap-3">
							<div className="flex items-center gap-2">
								<CreditCard className="h-4 w-4 text-muted-foreground" />
								<h2 className="text-sm font-semibold">
									Cobrança ({config.paymentGateway === "stripe" ? "Stripe" : "Asaas"})
								</h2>
							</div>
							{config.gatewayPronto ? (
								<Badge variant="secondary" className="gap-1">
									<CheckCircle2 className="h-3 w-3" />
									Funcionando em {config.gatewayAmbiente}
								</Badge>
							) : (
								<Badge variant="destructive" className="gap-1">
									<XCircle className="h-3 w-3" />
									Sem chave
								</Badge>
							)}
						</div>

						{!config.gatewayPronto && (
							<p className="flex gap-1.5 rounded-md bg-muted p-3 text-xs leading-relaxed">
								<AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
								Sem a chave, nenhuma fatura vira cobrança de verdade: a tela
								Financeiro dos seus clientes mostra “cobrança em preparo” e
								ninguém consegue pagar.
							</p>
						)}

						<div>
							<Label htmlFor="paymentGateway">Quem cobra</Label>
							<Select
								value={config.paymentGateway}
								onValueChange={(v) =>
									setConfig({
										...config,
										paymentGateway: v,
										// O Asaas é brasileiro e só cobra em real; deixar outra
										// moeda escrita aqui só faria a tela mentir.
										paymentCurrency:
											v === "asaas" ? "BRL" : config.paymentCurrency,
									})
								}
							>
								<SelectTrigger id="paymentGateway">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="asaas">
										Asaas — Pix, boleto e cartão, só em real
									</SelectItem>
									<SelectItem value="stripe">
										Stripe — cartão e meios locais, em qualquer moeda
									</SelectItem>
								</SelectContent>
							</Select>
							<p className="mt-1 text-xs leading-relaxed text-muted-foreground">
								Vale um de cada vez. Trocar não mexe nas faturas já emitidas:
								cada uma continua sendo acompanhada no gateway em que nasceu.
							</p>
						</div>

						<div>
							<Label htmlFor="fiscalCountry">Documento pedido no cadastro</Label>
							<Select
								value={config.fiscalCountry || "auto"}
								onValueChange={(v) =>
									setConfig({ ...config, fiscalCountry: v === "auto" ? "" : v })
								}
							>
								<SelectTrigger id="fiscalCountry">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="auto">Seguir a moeda da cobrança</SelectItem>
									{opcoesFiscais.map((o) => (
										<SelectItem key={o.pais} value={o.pais}>
											{o.nome} — {o.rotuloDocumento}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<p className="mt-1 text-xs leading-relaxed text-muted-foreground">
								Governa o que a tela de cadastro e o formulário de cartão pedem:
								CPF/CNPJ no Brasil, NIF em Portugal. Seguindo a moeda, cobrar
								em euro já passa a pedir NIF sozinho.
							</p>
						</div>

						{config.paymentGateway === "stripe" && (
							<>
								<div>
									<Label htmlFor="stripeSecretKey">Chave secreta</Label>
									<Input
										id="stripeSecretKey"
										type="password"
										autoComplete="new-password"
										placeholder={
											config.stripeSecretKeyDefinida
												? `Guardada — termina em ${config.stripeSecretKeyFinal}`
												: "sk_live_… ou sk_test_… (painel do Stripe → Desenvolvedores)"
										}
										value={novaChaveStripe}
										onChange={(e) => setNovaChaveStripe(e.target.value)}
									/>
									<p className="mt-1 text-xs leading-relaxed text-muted-foreground">
										{config.stripeSecretKeyDefinida &&
											"Deixe em branco para manter a que já está gravada. "}
										{config.chaveStripeVemDoEnv &&
											"Hoje a chave em uso vem do .env do servidor; gravar uma aqui faz ela valer no lugar. "}
										Uma chave <code>sk_test_</code> cobra de mentira — nada é
										debitado de ninguém.
									</p>
								</div>

								<div>
									<Label htmlFor="stripePublishableKey">
										Chave publicável
									</Label>
									<Input
										id="stripePublishableKey"
										placeholder="pk_live_… ou pk_test_…"
										value={config.stripePublishableKey || ""}
										onChange={(e) =>
											setConfig({
												...config,
												stripePublishableKey: e.target.value,
											})
										}
									/>
									<p className="mt-1 text-xs leading-relaxed text-muted-foreground">
										Esta é pública de propósito — vai para o navegador do
										cliente montar o formulário de cartão dentro do sistema.
										Sem ela, ninguém consegue cadastrar cartão.
									</p>
								</div>

								<div>
									<Label htmlFor="paymentCurrency">Moeda das cobranças</Label>
									<Select
										value={config.paymentCurrency}
										onValueChange={(v) =>
											setConfig({ ...config, paymentCurrency: v })
										}
									>
										<SelectTrigger id="paymentCurrency">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="EUR">EUR — euro</SelectItem>
											<SelectItem value="USD">USD — dólar</SelectItem>
											<SelectItem value="GBP">GBP — libra</SelectItem>
											<SelectItem value="BRL">BRL — real</SelectItem>
										</SelectContent>
									</Select>
									<p className="mt-1 text-xs leading-relaxed text-muted-foreground">
										O preço do plano é cobrado nesta moeda tal como está
										escrito: 90 vira € 90,00, não uma conversão de R$ 90. Sua
										conta do Stripe precisa aceitá-la.
									</p>
								</div>

								<div className="border-t pt-4">
									<Label htmlFor="stripeWebhookSecret">
										Segredo do webhook
									</Label>
									<Input
										id="stripeWebhookSecret"
										type="password"
										autoComplete="new-password"
										placeholder={
											config.stripeWebhookSecretDefinido
												? "Guardado — deixe em branco para manter"
												: "whsec_… (o Stripe mostra ao criar o endpoint)"
										}
										value={novoSegredoStripe}
										onChange={(e) => setNovoSegredoStripe(e.target.value)}
									/>
									<p className="mt-1 text-xs leading-relaxed text-muted-foreground">
										É o que prova que o aviso de pagamento veio mesmo do
										Stripe. Sem ele o webhook recusa tudo, e as faturas só
										baixam quando alguém clica em “verificar pagamento”.
									</p>

									<div className="mt-2">
										<Label className="text-xs text-muted-foreground">
											Endereço para cadastrar no Stripe
										</Label>
										<div className="mt-1 flex gap-2">
											<Input
												readOnly
												value={urlWebhookStripe}
												className="font-mono text-xs"
												onFocus={(e) => e.target.select()}
											/>
											<Button
												type="button"
												variant="outline"
												size="icon"
												aria-label="Copiar endereço do webhook do Stripe"
												onClick={async () => {
													try {
														await navigator.clipboard.writeText(
															urlWebhookStripe
														);
														toast.success("Endereço copiado.");
													} catch {
														toast.error("Não consegui copiar.");
													}
												}}
											>
												<Copy className="h-4 w-4" />
											</Button>
										</div>
										<p className="mt-1 text-xs leading-relaxed text-muted-foreground">
											Assine os eventos <code>invoice.paid</code>,{" "}
											<code>invoice.payment_failed</code>,{" "}
											<code>invoice.voided</code>,{" "}
											<code>payment_intent.succeeded</code> e{" "}
											<code>checkout.session.completed</code>.
										</p>
									</div>
								</div>
							</>
						)}

						{config.paymentGateway === "asaas" && (
						<>
						<div>
							<Label htmlFor="asaasApiKey">Chave da API</Label>
							<Input
								id="asaasApiKey"
								type="password"
								autoComplete="new-password"
								placeholder={
									config.asaasApiKeyDefinida
										? `Guardada — termina em ${config.asaasApiKeyFinal}`
										: "Cole a chave do painel do Asaas"
								}
								value={novaChave}
								onChange={(e) => setNovaChave(e.target.value)}
							/>
							<p className="mt-1 text-xs leading-relaxed text-muted-foreground">
								{config.asaasApiKeyDefinida &&
									"Deixe em branco para manter a que já está gravada."}
								{config.chaveVemDoEnv &&
									"Hoje a chave em uso vem do arquivo .env do servidor. Se você gravar uma aqui, ela passa a valer no lugar — e trocar o .env deixa de ter efeito."}
								{!config.asaasApiKeyDefinida &&
									!config.chaveVemDoEnv &&
									"Encontre em Configurações → Integrações, no painel do Asaas."}
							</p>
						</div>

						<div>
							<Label htmlFor="asaasEnv">Ambiente</Label>
							<Select
								value={config.asaasEnv}
								onValueChange={(v) => setConfig({ ...config, asaasEnv: v })}
							>
								<SelectTrigger id="asaasEnv">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="sandbox">
										Sandbox — cobranças de teste, ninguém paga de verdade
									</SelectItem>
									<SelectItem value="production">
										Produção — cobranças reais
									</SelectItem>
								</SelectContent>
							</Select>
							<p className="mt-1 text-xs text-muted-foreground">
								A chave é diferente em cada ambiente. Trocar aqui sem trocar a
								chave faz toda cobrança falhar.
							</p>
						</div>

						<div className="border-t pt-4">
							<Label htmlFor="asaasWebhookToken">Token do webhook</Label>
							<Input
								id="asaasWebhookToken"
								type="password"
								autoComplete="new-password"
								placeholder={
									config.asaasWebhookTokenDefinido
										? "Guardado — deixe em branco para manter"
										: "Invente uma senha e repita no painel do Asaas"
								}
								value={novoToken}
								onChange={(e) => setNovoToken(e.target.value)}
							/>
							<p className="mt-1 text-xs leading-relaxed text-muted-foreground">
								É o que impede alguém de marcar faturas como pagas mandando um
								POST. Sem ele, o webhook aceita qualquer chamada.
							</p>

							<div className="mt-2">
								<Label className="text-xs text-muted-foreground">
									Endereço para cadastrar no Asaas
								</Label>
								<div className="mt-1 flex gap-2">
									<Input
										readOnly
										value={urlWebhook}
										className="font-mono text-xs"
										onFocus={(e) => e.target.select()}
									/>
									<Button
										type="button"
										variant="outline"
										size="icon"
										aria-label="Copiar endereço do webhook"
										onClick={async () => {
											try {
												await navigator.clipboard.writeText(urlWebhook);
												toast.success("Endereço copiado.");
											} catch {
												toast.error("Não consegui copiar.");
											}
										}}
									>
										<Copy className="h-4 w-4" />
									</Button>
								</div>
							</div>
						</div>
						</>
						)}
					</div>

					{/* Teste */}
					<div className="space-y-3 rounded-xl border bg-card p-5">
						<h2 className="text-sm font-semibold">Período de teste</h2>
						<div>
							<Label htmlFor="trialDays">
								Dias de teste para uma empresa nova
							</Label>
							<div className="mt-1 flex items-center gap-2">
								<Input
									id="trialDays"
									type="number"
									min="1"
									className="w-24"
									value={config.trialDays}
									onChange={(e) =>
										setConfig({ ...config, trialDays: e.target.value })
									}
								/>
								<span className="text-sm text-muted-foreground">dias</span>
							</div>
							<p className="mt-1 text-xs leading-relaxed text-muted-foreground">
								Vale quando você inicia um teste sem dizer a quantidade de
								dias. Testes já em andamento não mudam.
							</p>
						</div>
					</div>

					<Button type="submit" disabled={salvando}>
						{salvando && <Loader2 className="h-4 w-4 animate-spin" />}
						Salvar
					</Button>
				</form>
			</div>
		</MainContainer>
	);
};

export default GlobalConfig;
